import { useCallback, useMemo, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { numberLocale, useLocale, useT } from '@/shared/i18n'
import { ToolPageShell } from '@/shared/tool-shell/ToolPageShell'
import { OptionToggle } from '@/shared/ui/OptionToggle'
import {
  MAX_TRANSLITERATION_CHARACTERS,
  TRANSLITERATION_SOURCES,
  transliterate,
  type TransliterationMode,
} from '@/pages/transliteraciya/transliteration'
import {
  reportTransliterationRejection,
  reportTransliterationSuccess,
} from '@/pages/transliteraciya/transliteration-analytics'

type CopyState = 'idle' | 'copied' | 'error'

export function TransliterationPage() {
  return (
    <ToolPageShell toolKey="transliteration">
      <TransliterationForm />
    </ToolPageShell>
  )
}

export function TransliterationForm() {
  const t = useT()
  const { locale } = useLocale()
  const localeCode = numberLocale(locale)
  const p = t.transliteration
  const [input, setInput] = useState('')
  const [mode, setMode] = useState<TransliterationMode>('icao')
  const [error, setError] = useState<'character_limit' | null>(null)
  const [copyState, setCopyState] = useState<CopyState>('idle')
  const reportedInputRef = useRef(false)
  const revisionRef = useRef(0)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const attempt = useMemo(() => transliterate(input, mode), [input, mode])
  const output = attempt.status === 'success' ? attempt.output : ''
  const modeOptions: Array<{
    value: TransliterationMode
    label: string
    description: string
  }> = [
    { value: 'icao', label: p.modes.icao.label, description: p.modes.icao.description },
    { value: 'gost', label: p.modes.gost.label, description: p.modes.gost.description },
    { value: 'slug', label: p.modes.slug.label, description: p.modes.slug.description },
    { value: 'reverse', label: p.modes.reverse.label, description: p.modes.reverse.description },
  ]

  const handleInput = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const startedAt = performance.now()
    const next = event.target.value
    const nextAttempt = transliterate(next, mode)
    if (nextAttempt.status === 'error') {
      setError(nextAttempt.error)
      reportTransliterationRejection()
      return
    }

    revisionRef.current += 1
    setInput(next)
    setError(null)
    setCopyState('idle')
    if (!reportedInputRef.current && next.length > 0) {
      reportTransliterationSuccess(performance.now() - startedAt)
      reportedInputRef.current = true
    }
  }, [mode])

  const handleMode = useCallback((nextMode: TransliterationMode) => {
    if (nextMode === mode) return
    const startedAt = performance.now()
    const nextAttempt = transliterate(input, nextMode)
    revisionRef.current += 1
    setMode(nextMode)
    setCopyState('idle')
    if (input.length > 0 && nextAttempt.status === 'success') {
      reportTransliterationSuccess(performance.now() - startedAt)
    }
  }, [input, mode])

  const handleReset = useCallback(() => {
    revisionRef.current += 1
    setInput('')
    setMode('icao')
    setError(null)
    setCopyState('idle')
    reportedInputRef.current = false
    inputRef.current?.focus()
  }, [])

  const handleCopy = useCallback(async () => {
    if (!output || !navigator.clipboard) {
      setCopyState('error')
      return
    }
    const revision = revisionRef.current
    try {
      await navigator.clipboard.writeText(output)
      if (revision === revisionRef.current) setCopyState('copied')
    } catch {
      if (revision === revisionRef.current) setCopyState('error')
    }
  }, [output])

  return (
    <>
      <div className="grid gap-4 lg:grid-cols-2 lg:gap-6">
        <div className="min-w-0 space-y-3">
          <Card className="overflow-hidden shadow-sm">
            <div className="flex min-h-14 items-center justify-between gap-3 border-b bg-muted/45 px-4 py-2">
              <div>
                <label htmlFor="transliteration-input" className="text-sm font-medium">
                  {mode === 'reverse' ? p.latinInput : p.russianInput}
                </label>
                <div className="text-xs text-muted-foreground">
                  {[...input].length.toLocaleString(localeCode)} /{' '}
                  {MAX_TRANSLITERATION_CHARACTERS.toLocaleString(localeCode)}
                </div>
              </div>
              {(input || mode !== 'icao') && (
                <Button variant="outline" size="sm" onClick={handleReset}>
                  {p.reset}
                </Button>
              )}
            </div>
            <textarea
              ref={inputRef}
              id="transliteration-input"
              value={input}
              onChange={handleInput}
              placeholder={mode === 'reverse' ? p.reversePlaceholder : p.inputPlaceholder}
              aria-describedby={error ? 'transliteration-error' : 'transliteration-local-note'}
              className={cn(
                'ym-disable-keys ym-disable-submit block min-h-[160px] w-full resize-y border-0 bg-card p-4 sm:p-5 lg:min-h-[300px]',
                'font-mono text-[15px] leading-7 placeholder:text-muted-foreground/55',
                'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary/35',
              )}
            />
            {error && (
              <div id="transliteration-error" role="alert" className="border-t bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {p.characterLimitError}
              </div>
            )}
          </Card>

          <fieldset className="grid grid-cols-2 gap-2">
            <legend className="sr-only">{p.modeTitle}</legend>
            {modeOptions.map((option) => (
              <OptionToggle
                key={option.value}
                type="radio"
                name="transliteration-mode"
                label={option.label}
                description={option.description}
                checked={mode === option.value}
                onChange={() => handleMode(option.value)}
              />
            ))}
          </fieldset>
        </div>

        <Card className="flex min-w-0 flex-col overflow-hidden shadow-sm">
          <div className="flex min-h-14 items-center justify-between gap-3 border-b bg-muted/45 px-4 py-2">
            <div id="transliteration-result-label" className="text-sm font-medium">
              {p.result}
            </div>
            {output && (
              <Button variant="outline" size="sm" onClick={handleCopy}>
                {copyState === 'copied' ? p.copied : copyState === 'error' ? p.copyError : p.copy}
              </Button>
            )}
          </div>
          <div
            role="region"
            aria-labelledby="transliteration-result-label"
            className="ym-hide-content min-h-[160px] flex-1 overflow-auto whitespace-pre-wrap break-words bg-card p-4 font-mono text-[15px] leading-7 sm:p-5 lg:min-h-[300px]"
          >
            {output || <span className="text-muted-foreground/55">{p.outputPlaceholder}</span>}
          </div>
          <p className="sr-only" aria-live="polite">
            {output ? p.ready : ''}
          </p>
        </Card>
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <Card className="border-amber-300/70 bg-amber-50/60 shadow-sm dark:border-amber-800 dark:bg-amber-950/25">
          <CardContent className="p-4">
            <h2 className="text-sm font-semibold">{p.officialTitle}</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">{p.officialText}</p>
          </CardContent>
        </Card>
        <Card className="border-dashed bg-card/70 shadow-sm">
          <CardContent className="p-4">
            <h2 className="text-sm font-semibold">{p.sourcesTitle}</h2>
            <p id="transliteration-local-note" className="mt-2 text-sm leading-6 text-muted-foreground">
              {p.localText}
            </p>
            <div className="mt-3 flex flex-wrap gap-3 text-sm">
              <a className="font-medium text-primary hover:underline" href={TRANSLITERATION_SOURCES.icao} rel="noreferrer" target="_blank">
                ICAO Doc 9303
              </a>
              <a className="font-medium text-primary hover:underline" href={TRANSLITERATION_SOURCES.gost} rel="noreferrer" target="_blank">
                ГОСТ Р 7.0.34-2014
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  )
}
